import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { ENTITIES } from 'src/constants/entityConsts';

export const DRAWER_IDS = {
  INTERNAL_CHAT: 'INTERNAL_CHAT',
  NOTES: 'NOTES',
  DETAILS: 'DETAILS',
} as const;

export type DrawerId = (typeof DRAWER_IDS)[keyof typeof DRAWER_IDS];

// CLEANUP(EnableContextBarVisualUpgrades): Keep these when flag is removed
export const DRAWER_LABELS: Record<DrawerId, string> = {
  [DRAWER_IDS.INTERNAL_CHAT]: 'Internal chat',
  [DRAWER_IDS.NOTES]: 'Notes',
  [DRAWER_IDS.DETAILS]: 'Details',
};

export interface DrawerActionConfig {
  icon: string;
  label: string;
  actionType: 'create_note';
}

export const DRAWER_ACTION_CONFIGS: Partial<
  Record<DrawerId, DrawerActionConfig>
> = {
  [DRAWER_IDS.NOTES]: {
    icon: 'Plus',
    label: 'Create note',
    actionType: 'create_note',
  },
};

export const ENTITY_TYPES = {
  CLIENT_USER: ENTITIES.CLIENT_USER,
  CLIENT_COMPANY: ENTITIES.CLIENT_COMPANY,
} as const;

export type EntityType = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

export interface ContextBarTarget {
  id: string;
  entityType: EntityType;
}

export type DrawerVisibilityConfig = Partial<Record<DrawerId, boolean>>;

const DEFAULT_DRAWER_VISIBILITY: Record<DrawerId, boolean> = {
  [DRAWER_IDS.INTERNAL_CHAT]: true,
  [DRAWER_IDS.NOTES]: true,
  [DRAWER_IDS.DETAILS]: true,
};

export interface ContextBarPageTransition {
  target: ContextBarTarget;
  drawerConfig?: DrawerVisibilityConfig;
}

interface ContextBarState {
  target: ContextBarTarget | null;
  drawerVisibilityConfig: Record<DrawerId, boolean>;
  lastOpenDrawer: DrawerId | null;
}

interface ContextBarContextValue extends ContextBarState {
  setTransition: (transition: ContextBarPageTransition) => void;
  setLastOpenDrawer: (drawerId: DrawerId) => void;
  getActiveDrawer: (hasAssistantTab: boolean) => DrawerId;
}

const defaultContextValue: ContextBarContextValue = {
  target: null,
  drawerVisibilityConfig: DEFAULT_DRAWER_VISIBILITY,
  lastOpenDrawer: null,
  setTransition: () => {},
  setLastOpenDrawer: () => {},
  getActiveDrawer: () => DRAWER_IDS.INTERNAL_CHAT,
};

export const ContextBarContext =
  createContext<ContextBarContextValue>(defaultContextValue);

export const ContextBarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [target, setTarget] = useState<ContextBarTarget | null>(null);
  const [drawerVisibilityConfig, setDrawerVisibilityConfig] = useState<
    Record<DrawerId, boolean>
  >(DEFAULT_DRAWER_VISIBILITY);
  const [lastOpenDrawer, setLastOpenDrawerState] = useState<DrawerId | null>(
    null,
  );

  const setTransition = useCallback((transition: ContextBarPageTransition) => {
    setTarget(transition.target);
    setDrawerVisibilityConfig({
      ...DEFAULT_DRAWER_VISIBILITY,
      ...transition.drawerConfig,
    });
  }, []);

  const setLastOpenDrawer = useCallback((drawerId: DrawerId) => {
    setLastOpenDrawerState(drawerId);
  }, []);

  /**
   * Determines which drawer should be active based on:
   * 1. User's last selected drawer (if still visible)
   * 2. Default drawer based on permissions
   */
  const getActiveDrawer = useCallback(
    (hasAssistantTab: boolean): DrawerId => {
      // If user has selected a drawer and it's still visible, use it
      if (lastOpenDrawer && drawerVisibilityConfig[lastOpenDrawer]) {
        // Special case: INTERNAL_CHAT requires hasAssistantTab permission
        if (lastOpenDrawer === DRAWER_IDS.INTERNAL_CHAT && !hasAssistantTab) {
          // Fall through to default
        } else {
          return lastOpenDrawer;
        }
      }

      // Default: INTERNAL_CHAT if available, otherwise NOTES
      return hasAssistantTab ? DRAWER_IDS.INTERNAL_CHAT : DRAWER_IDS.NOTES;
    },
    [lastOpenDrawer, drawerVisibilityConfig],
  );

  const value = useMemo<ContextBarContextValue>(
    () => ({
      target,
      drawerVisibilityConfig,
      lastOpenDrawer,
      setTransition,
      setLastOpenDrawer,
      getActiveDrawer,
    }),
    [
      target,
      drawerVisibilityConfig,
      lastOpenDrawer,
      setTransition,
      setLastOpenDrawer,
      getActiveDrawer,
    ],
  );

  return (
    <ContextBarContext.Provider value={value}>
      {children}
    </ContextBarContext.Provider>
  );
};

export const useContextBarContext = (): ContextBarContextValue => {
  return useContext(ContextBarContext);
};
